import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../../context/configurationContext';
import { parse, stringify } from '../../../util/urlHelpers';

import css from './CategoryBreadcrumb.module.css';

const MAX_CATEGORY_LEVELS = 3;

/**
 * Breadcrumb trail of the active category path, rendered above
 * ActiveFilterChips on SearchPage. Reads `pub_categoryLevel1`-`3`
 * from the URL, resolves each id to its display name via
 * `categoryConfiguration`, and renders one clickable segment per
 * level.
 *
 * Clicking a segment narrows the URL to that level (clears every
 * deeper level), so the buyer can step back up the tree without
 * leaving the search results. The leaf segment is clickable too —
 * it just keeps the buyer at the same URL.
 *
 * Renders nothing when no category is active (free-text or
 * location-only searches).
 */
const CategoryBreadcrumb = () => {
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();

  const query = parse(location.search);

  // Walk the URL params + category tree in parallel to collect
  // { id, name, level } for each breadcrumb segment. Stop at the
  // first missing param OR the first id that's not in the tree
  // (so a stale deep link doesn't render a broken breadcrumb).
  const segments = [];
  let pool = config?.categoryConfiguration?.categories || [];
  for (let i = 0; i < MAX_CATEGORY_LEVELS; i++) {
    const key = `pub_categoryLevel${i + 1}`;
    const id = query[key];
    if (!id) {
      break;
    }
    const match = pool.find(c => c.id === id);
    if (!match) {
      break;
    }
    segments.push({ id: match.id, name: match.name, level: i + 1 });
    pool = match.subcategories || [];
  }

  if (segments.length === 0) {
    return null;
  }

  const goToLevel = level => {
    const next = { ...query };
    for (let i = level + 1; i <= MAX_CATEGORY_LEVELS; i++) {
      delete next[`pub_categoryLevel${i}`];
    }
    const search = stringify(next);
    history.push({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  return (
    <ul className={css.root} aria-label="Category breadcrumb">
      {segments.map(segment => (
        <li key={segment.id} className={css.item}>
          <button
            type="button"
            className={css.segmentButton}
            onClick={() => goToLevel(segment.level)}
          >
            {segment.name}
          </button>
        </li>
      ))}
    </ul>
  );
};

export default CategoryBreadcrumb;
